import { ChartAreaInteractive } from "@/components/chart-area-interactive";
import { ChartLoginsPerDay } from "@/components/chart-logins-per-day";
import { ChartUserLoginTimes } from "@/components/chart-user-login-times";
import { DataTable } from "@/components/data-table";
import { SiteHeader } from "@/components/site-header";
import { StatisticsCard } from "@/components/statistics-card";
import { MonetaryStatisticsCard } from "@/components/monetary-statistics-card";
import { ReportQueueAlert } from "@/components/report-queue-alert";
import { TimeRangeSelector } from "@/components/time-range-selector";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { queryKeys } from "@/lib/queryKeys";
import { adminProvider } from "@/providers/admin.provider";
import { countersProvider } from "@/providers/counters.provider";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import data from "@/app/dashboard/data.json";

export function DashboardPage() {
  const [days, setDays] = useState(90);

  const { data: onlineUsers, isLoading: loadingOnline } = useQuery({
    queryKey: ["admin-users-current"],
    queryFn: () => adminProvider.getUsersCurrent(),
    refetchInterval: 10_000,
    staleTime: 5_000,
  });

  const { data: usersHistoryResp, isLoading: loadingHistory } = useQuery({
    queryKey: ["admin-users-history", days],
    queryFn: () => adminProvider.getUsersHistory(days),
    staleTime: 60_000,
  });
  const usersHistory = usersHistoryResp?.history;

  const { data: usersHistoryAllResp, isLoading: loadingHistoryAll } = useQuery(
    {
      queryKey: ["admin-users-history-all"],
      queryFn: () => adminProvider.getUsersHistoryAll(),
      staleTime: 60_000,
    },
  );
  const usersHistoryAll = usersHistoryAllResp?.history;

  // Only the all-time rows: the counter also writes a daily series, and
  // summing both would double count.
  const distanceParams = {
    name: "distance_traveled_by_user_meters",
    period: "all",
  } as const;
  const { data: distanceTotals, isLoading: loadingDistance } = useQuery({
    queryKey: queryKeys.counterTotals(distanceParams),
    queryFn: () => countersProvider.getTotals(distanceParams),
    refetchInterval: 10_000,
    staleTime: 5_000,
  });

  const totalDistanceMeters =
    distanceTotals?.reduce((sum, t) => sum + t.total, 0) ?? 0;

  const [ignoreSpecialUsers, setIgnoreSpecialUsers] = useState(true);

  const { data: loginTimeHeatmap, isLoading: loadingLoginTimeHeatmap } =
    useQuery({
      queryKey: queryKeys.globalLoginTimeHeatmap(days, ignoreSpecialUsers),
      queryFn: () => adminProvider.getLoginTimeHeatmap(days, ignoreSpecialUsers),
      staleTime: 60_000,
    });

  const {
    data: loginsPerDay,
    isLoading: loadingLoginsPerDay,
    isError: loginsPerDayError,
  } = useQuery({
    queryKey: queryKeys.globalLoginsPerDay(days, ignoreSpecialUsers),
    queryFn: () => adminProvider.getLoginsPerDay(days, ignoreSpecialUsers),
    staleTime: 60_000,
  });

  return (
    <>
      <SiteHeader title="Dashboard" />
      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            {/* Needs-attention alert for the safety report queue */}
            <ReportQueueAlert />

            {/* Time range selector + special users toggle for the login charts */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 lg:px-6">
              <TimeRangeSelector days={days} onDaysChange={setDays} />
              <div className="flex min-h-9 min-w-0 items-center gap-2">
                <Switch
                  id="ignore-special-users"
                  checked={ignoreSpecialUsers}
                  onCheckedChange={setIgnoreSpecialUsers}
                />
                <Label
                  htmlFor="ignore-special-users"
                  className="min-h-9 min-w-0 cursor-pointer text-sm text-muted-foreground"
                >
                  Exclude special users from login charts
                </Label>
              </div>
            </div>

            {/* Live stat + mock cards */}
            <div className="flex flex-col gap-4">
              <StatisticsCard
                onlineUsers={onlineUsers}
                totalUsers={onlineUsers}
                loadingOnline={loadingOnline}
                usersHistory={usersHistory}
                days={days}
                allHistoryData={usersHistoryAll}
                totalDistance={totalDistanceMeters}
                loadingDistance={loadingDistance}
              />

              <MonetaryStatisticsCard days={days} />
            </div>

            {/* New registrations chart — real data, shared days state */}
            <div className="px-4 lg:px-6">
              <ChartAreaInteractive
                data={usersHistory ?? []}
                days={days}
                onDaysChange={setDays}
                isLoading={loadingHistory}
                onlineUsers={onlineUsers}
                rangeTotal={usersHistoryResp?.total}
                allTimeTotal={usersHistoryAllResp?.total}
              />
            </div>

            {/* Login times — when logins happen across the day, all users */}
            <div className="px-4 lg:px-6">
              <ChartUserLoginTimes
                data={loginTimeHeatmap ?? []}
                days={days}
                isLoading={loadingLoginTimeHeatmap}
              />
            </div>

            {/* Logins per day — daily login volume, all users */}
            <div className="px-4 lg:px-6">
              <ChartLoginsPerDay
                data={loginsPerDay ?? []}
                days={days}
                isLoading={loadingLoginsPerDay}
                isError={loginsPerDayError}
              />
            </div>

            <DataTable data={data} />
          </div>
        </div>
      </div>
    </>
  );
}
